/**
 * Capabilities — Business-level feature toggles (Source of Truth for Admin).
 *
 * The Admin Plan Matrix manages CAPABILITIES (16 toggles) instead of 52
 * granular feature keys. Each granular FEATURE_KEY maps to exactly ONE
 * capability via KEY_TO_CAPABILITY. The runtime resolver in
 * `feature-access.js` reads `matrix.plans[plan].capabilities[capKey]`.
 *
 * Why: prevents the broken state where a related set of surfaces
 * (e.g. Sessions module + Session Packages + Catalog + Landing) is
 * partially open and partially locked. One toggle = entire capability.
 *
 * IMPORTANT: do NOT add granular keys here. If a new key is added in
 * `feature-keys.js`, decide which capability it belongs to and add a
 * row in KEY_TO_CAPABILITY below.
 */
import { FEATURE_KEYS as K } from "./feature-keys";

export const CAPABILITIES = {
  CORE_AUTHORING: "cap_core_authoring",
  COMMERCE: "cap_commerce",
  MESSAGING: "cap_messaging",
  EMAILS: "cap_emails",
  SESSIONS: "cap_sessions",
  QUIZZES: "cap_quizzes",
  CERTIFICATES: "cap_certificates",
  VIP: "cap_vip",
  GRANTS: "cap_grants",
  LFA: "cap_lfa",
  BRANDING_PRO: "cap_branding_pro",
  CUSTOM_HTML: "cap_custom_html",
  SEO: "cap_seo",
  ANALYTICS_PRO: "cap_analytics_pro",
  BANNERS: "cap_banners",
  DISCUSSIONS: "cap_discussions",
  TRACKING_INTEGRATIONS: "cap_tracking_integrations",
};

/**
 * Capabilities that are part of every plan and cannot be toggled off
 * by the admin matrix UI.
 */
export const CORE_CAPABILITIES = new Set([
  CAPABILITIES.CORE_AUTHORING,
  CAPABILITIES.COMMERCE,
  CAPABILITIES.MESSAGING,
]);

/**
 * Mapping: granular FEATURE_KEY → capability.
 * EVERY key in feature-keys.js MUST have a row here.
 */
export const KEY_TO_CAPABILITY = {
  // ── core authoring ────────────────────────────────────────────────────────
  [K.ACADEMY_SITE]: CAPABILITIES.CORE_AUTHORING,
  [K.UNLIMITED_COURSES]: CAPABILITIES.CORE_AUTHORING,
  [K.UNLIMITED_STUDENTS]: CAPABILITIES.CORE_AUTHORING,
  [K.VIDEO_LESSONS]: CAPABILITIES.CORE_AUTHORING,
  [K.FILE_LESSONS]: CAPABILITIES.CORE_AUTHORING,
  [K.TEXT_LESSONS]: CAPABILITIES.CORE_AUTHORING,
  [K.PROGRESS_TRACKING]: CAPABILITIES.CORE_AUTHORING,
  [K.DRIP_CONTENT]: CAPABILITIES.CORE_AUTHORING,
  [K.BRANDING]: CAPABILITIES.CORE_AUTHORING,
  [K.PAGE_BUILDER_ACADEMY]: CAPABILITIES.CORE_AUTHORING,
  [K.PAGE_BUILDER_PRODUCT]: CAPABILITIES.CORE_AUTHORING,
  [K.PAGE_BUILDER_COURSE]: CAPABILITIES.CORE_AUTHORING,
  [K.TEAM_MANAGEMENT]: CAPABILITIES.CORE_AUTHORING,

  // ── commerce ──────────────────────────────────────────────────────────────
  [K.STRIPE_PAYMENTS]: CAPABILITIES.COMMERCE,
  [K.BANK_TRANSFER]: CAPABILITIES.COMMERCE,
  [K.ONE_TIME_PRODUCTS]: CAPABILITIES.COMMERCE,
  [K.BUNDLES]: CAPABILITIES.COMMERCE,
  [K.DIGITAL_PRODUCTS]: CAPABILITIES.COMMERCE,
  [K.RECURRING_PRODUCTS]: CAPABILITIES.COMMERCE,
  [K.COUPONS]: CAPABILITIES.COMMERCE,
  [K.RECEIPTS]: CAPABILITIES.COMMERCE,

  // ── messaging ─────────────────────────────────────────────────────────────
  [K.MESSAGING]: CAPABILITIES.MESSAGING,
  [K.GROUP_MESSAGING]: CAPABILITIES.MESSAGING,
  [K.NOTIFICATION_HISTORY]: CAPABILITIES.MESSAGING,
  [K.SENT_INVITES]: CAPABILITIES.MESSAGING,

  // ── emails ────────────────────────────────────────────────────────────────
  [K.AUTOMATED_EMAILS]: CAPABILITIES.EMAILS,
  [K.EMAIL_TEMPLATES]: CAPABILITIES.EMAILS,
  [K.EMAIL_CAMPAIGNS]: CAPABILITIES.EMAILS,
  [K.EMAIL_LOGS]: CAPABILITIES.EMAILS,

  // ── sessions ──────────────────────────────────────────────────────────────
  [K.LIVE_SESSIONS]: CAPABILITIES.SESSIONS,
  [K.RECURRING_SESSIONS]: CAPABILITIES.SESSIONS,
  [K.SESSION_PACKAGES]: CAPABILITIES.SESSIONS,
  [K.SESSION_LANDING]: CAPABILITIES.SESSIONS,
  [K.SESSIONS_CATALOG]: CAPABILITIES.SESSIONS,
  [K.GOOGLE_CALENDAR_SYNC]: CAPABILITIES.SESSIONS,

  // ── quizzes ───────────────────────────────────────────────────────────────
  [K.QUIZZES]: CAPABILITIES.QUIZZES,
  [K.AI_QUIZZES]: CAPABILITIES.QUIZZES,
  [K.QUESTION_BANK]: CAPABILITIES.QUIZZES,
  [K.QUIZ_BLOCKING]: CAPABILITIES.QUIZZES,

  // ── certificates ──────────────────────────────────────────────────────────
  [K.CERTIFICATES_AUTO]: CAPABILITIES.CERTIFICATES,
  [K.CERTIFICATES_MANUAL]: CAPABILITIES.CERTIFICATES,
  [K.CERTIFICATES_PRO_EXTRAS]: CAPABILITIES.CERTIFICATES,

  // ── VIP ───────────────────────────────────────────────────────────────────
  [K.VIP_MEMBERSHIP]: CAPABILITIES.VIP,

  // ── grants ────────────────────────────────────────────────────────────────
  [K.GRANTS_AND_INVITES]: CAPABILITIES.GRANTS,

  // ── limited free access ───────────────────────────────────────────────────
  [K.LIMITED_FREE_ACCESS]: CAPABILITIES.LFA,

  // ── branding pro ──────────────────────────────────────────────────────────
  [K.CUSTOM_DOMAIN]: CAPABILITIES.BRANDING_PRO,
  [K.HIDE_DWRRAH_ATTRIBUTION]: CAPABILITIES.BRANDING_PRO,

  // ── custom html ───────────────────────────────────────────────────────────
  [K.CUSTOM_HTML_BLOCK]: CAPABILITIES.CUSTOM_HTML,
  [K.HTML_TEMPLATES]: CAPABILITIES.CUSTOM_HTML,

  // ── seo ───────────────────────────────────────────────────────────────────
  [K.SEO_LANDING]: CAPABILITIES.SEO,

  // ── analytics ─────────────────────────────────────────────────────────────
  [K.ADVANCED_ANALYTICS]: CAPABILITIES.ANALYTICS_PRO,

  // ── tracking integrations (visitor pixels / analytics SDKs) ──────────────
  [K.TRACKING_INTEGRATIONS]: CAPABILITIES.TRACKING_INTEGRATIONS,

  // ── banners ───────────────────────────────────────────────────────────────
  [K.BANNERS]: CAPABILITIES.BANNERS,

  // ── discussions ───────────────────────────────────────────────────────────
  [K.COURSE_DISCUSSIONS]: CAPABILITIES.DISCUSSIONS,
};

/**
 * Display metadata for capabilities (Admin matrix UI + marketing surfaces).
 * Each entry lists the granular keys it consolidates so the admin sees
 * exactly what the toggle controls.
 */
export const CAPABILITY_METADATA = {
  [CAPABILITIES.CORE_AUTHORING]: {
    label_ar: "بناء المحتوى الأساسي",
    label_en: "Core authoring",
    description_ar: "كل ما تحتاجه لبناء أكاديميتك ودوراتك (مفعّلة دائماً).",
    description_en: "Everything needed to build your academy and courses (always on).",
    icon: "BookOpen",
  },
  [CAPABILITIES.COMMERCE]: {
    label_ar: "البيع والمدفوعات",
    label_en: "Commerce & payments",
    description_ar: "Stripe + التحويل البنكي + المنتجات والكوبونات والإيصالات.",
    description_en: "Stripe + bank transfer + products, coupons & receipts.",
    icon: "CreditCard",
  },
  [CAPABILITIES.MESSAGING]: {
    label_ar: "المراسلة والإشعارات",
    label_en: "Messaging & notifications",
    description_ar: "رسائل مباشرة وجماعية + سجل الإشعارات + الدعوات.",
    description_en: "Direct & group messages + notification history + invites.",
    icon: "MessageCircle",
  },
  [CAPABILITIES.EMAILS]: {
    label_ar: "نظام البريد الإلكتروني",
    label_en: "Email system",
    description_ar: "إيميلات تلقائية + قوالب + حملات + سجلات إرسال.",
    description_en: "Automated emails + templates + campaigns + send logs.",
    icon: "Mail",
  },
  [CAPABILITIES.SESSIONS]: {
    label_ar: "الجلسات الحية",
    label_en: "Live sessions",
    description_ar: "جلسات حية + متكرّرة + رزم + كاتالوج عام + صفحة هبوط + مزامنة Google Calendar.",
    description_en: "Live + recurring + packages + public catalog + landing + Google Calendar sync.",
    icon: "Video",
    highlight: true,
  },
  [CAPABILITIES.QUIZZES]: {
    label_ar: "الكويزات والاختبارات",
    label_en: "Quizzes & exams",
    description_ar: "كويزات + اختبارات AI + بنك أسئلة + حجب التقدّم.",
    description_en: "Quizzes + AI generation + question bank + progress blocking.",
    icon: "ListChecks",
    highlight: true,
  },
  [CAPABILITIES.CERTIFICATES]: {
    label_ar: "الشهادات",
    label_en: "Certificates",
    description_ar: "إصدار تلقائي ويدوي + قوالب وتوقيع وتخصيص متقدّم.",
    description_en: "Auto + manual issuance + templates, signature & advanced customization.",
    icon: "Award",
  },
  [CAPABILITIES.VIP]: {
    label_ar: "عضويات VIP",
    label_en: "VIP memberships",
    description_ar: "اشتراك واحد يفتح كل المحتوى — صفحة هبوط مخصّصة + تحليلات.",
    description_en: "One subscription unlocking everything — landing page + analytics.",
    icon: "Crown",
    highlight: true,
  },
  [CAPABILITIES.GRANTS]: {
    label_ar: "المنح والدعوات",
    label_en: "Grants & invites",
    description_ar: "منح المنتجات والجلسات للطلاب + الدعوات المخصّصة.",
    description_en: "Grant products and sessions + targeted invites.",
    icon: "Send",
  },
  [CAPABILITIES.LFA]: {
    label_ar: "الوصول المجاني المحدود",
    label_en: "Limited free access",
    description_ar: "حوّل الزوار لعملاء بفترة وصول مجانية محدودة.",
    description_en: "Convert visitors to customers with a limited free access window.",
    icon: "Gift",
  },
  [CAPABILITIES.BRANDING_PRO]: {
    label_ar: "النطاق المخصّص + إخفاء العلامة",
    label_en: "Custom domain + hide attribution",
    description_ar: "نطاقك الخاص (yourname.com) + إخفاء عبارة «صُنع بـ 💜 بواسطة منصة دورة».",
    description_en: "Your own domain + hide the \"Made with 💜 by Dwrrah\" attribution.",
    icon: "Globe",
  },
  [CAPABILITIES.CUSTOM_HTML]: {
    label_ar: "HTML/Embed مخصّص + قوالب",
    label_en: "Custom HTML/Embed + templates",
    description_ar: "كتل HTML مخصّصة + قوالب جاهزة لصفحات الهبوط.",
    description_en: "Custom HTML blocks + ready-made landing templates.",
    icon: "Code",
  },
  [CAPABILITIES.SEO]: {
    label_ar: "تحسين الظهور (SEO/AEO)",
    label_en: "SEO / AEO",
    description_ar: "تحسين ظهور صفحات الأكاديمية والمنتجات في Google.",
    description_en: "Improve visibility of academy & product pages in Google.",
    icon: "Search",
  },
  [CAPABILITIES.ANALYTICS_PRO]: {
    label_ar: "التحليلات والتقارير المتقدّمة",
    label_en: "Advanced analytics",
    description_ar: "تقارير متقدّمة عن الإيرادات والطلاب والأداء.",
    description_en: "Advanced reports on revenue, students and performance.",
    icon: "BarChart3",
  },
  [CAPABILITIES.BANNERS]: {
    label_ar: "البانرات والإعلانات",
    label_en: "Banners & announcements",
    description_ar: "اعرض إعلانات وعروض ترويجية لزوار وطلاب أكاديميتك.",
    description_en: "Show announcements and promos to academy visitors and students.",
    icon: "Megaphone",
  },
  [CAPABILITIES.DISCUSSIONS]: {
    label_ar: "نقاشات الدورة",
    label_en: "Course discussions",
    description_ar: "نقاشات داخل صفحات الدورة بين الطلاب والمدرّب.",
    description_en: "Discussions inside course pages between students & instructor.",
    icon: "MessageSquare",
  },
  [CAPABILITIES.TRACKING_INTEGRATIONS]: {
    label_ar: "أدوات التتبع والتحليلات",
    label_en: "Tracking & analytics integrations",
    description_ar: "Meta Pixel + Google Analytics + GTM + TikTok + Snap + Microsoft Clarity + PostHog لصفحة هبوط الأكاديمية.",
    description_en: "Meta Pixel + Google Analytics + GTM + TikTok + Snap + Microsoft Clarity + PostHog on the academy landing.",
    icon: "Activity",
  },
};

export const ALL_CAPABILITIES = Object.values(CAPABILITIES);

/** Resolve the capability key for a granular feature key. */
export function getCapabilityForKey(key) {
  return KEY_TO_CAPABILITY[key] || null;
}

/** Read inclusion bool for a capability + plan from the matrix. */
export function isCapabilityInPlan(matrix, planCode, capKey) {
  if (!matrix || !planCode || !capKey) return false;
  return matrix?.plans?.[planCode]?.capabilities?.[capKey] === true;
}

/**
 * Build a fresh `capabilities` block for a plan by ORing all the granular
 * `features` keys that belong to each capability. Used by:
 *   - DB migration seed
 *   - Admin save mirror (capabilities → features write-through)
 */
export function deriveCapabilitiesFromFeatures(featuresObj) {
  const out = {};
  for (const cap of ALL_CAPABILITIES) out[cap] = false;
  if (!featuresObj) return out;
  for (const [key, val] of Object.entries(featuresObj)) {
    if (val !== true) continue;
    const cap = KEY_TO_CAPABILITY[key];
    if (cap) out[cap] = true;
  }
  // Core capabilities: always on regardless.
  for (const cap of CORE_CAPABILITIES) out[cap] = true;
  return out;
}

/**
 * Mirror: given a capabilities block, regenerate the granular `features`
 * map so legacy reads keep working during the transition window.
 */
export function deriveFeaturesFromCapabilities(capsObj) {
  const out = {};
  for (const [key, cap] of Object.entries(KEY_TO_CAPABILITY)) {
    out[key] = capsObj?.[cap] === true;
  }
  return out;
}
